import { Disclosure } from "@headlessui/react";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { getAdminList } from "../api";
import { isLoading, setLoading } from "../redux/loadingSlice";
import { login, userLogout } from "../api/auth";
import { FORM_TEXTS } from "../constants";
import {
  LogoutIcon,
  PageLoader,
  AddNotification,
  HelpIcon,
} from "../components/core";
import { clearUser, setUser } from "../redux/userSlice";
import { clearNotification } from "../redux/notificationSlice";

export const NavBar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.profile);
  const location = useLocation();
  const loading = useSelector(isLoading);
  const notification = useSelector((state) => state.notification?.notification);

  const onLogin = async () => {
    login();
    dispatch(setLoading(true));
  };

  const onLogout = useCallback(
    async (path) => {
      dispatch(clearUser());
      sessionStorage.removeItem("profile");
      await userLogout(path);
    },
    [dispatch]
  );

  useEffect(() => {
    const fetchAdminDetails = async (isid) => {
      const req = { operation: "fetchAdmin" };
      const adminResp = await getAdminList(req);
      if (adminResp?.responseCode === 200) {
        const admin = adminResp?.responseData.filter(
          (admin) => admin.isId.toLowerCase() === isid.toLowerCase()
        );
        if (admin.length > 0) {
          let profile = {
            ...userInfo,
            role: admin[0].role,
            name: admin[0].name,
          };
          dispatch(setUser(profile));
          dispatch(setLoading(false));
          sessionStorage.setItem("profile", JSON.stringify(profile));
          navigate("/");
        } else {
          await onLogout("/forbidden");
        }
      }
    };
    if (userInfo && !userInfo.role) {
      fetchAdminDetails(userInfo.isid);
    }
    if (userInfo?.name) {
      dispatch(setLoading(false));
    }
  }, [userInfo, dispatch, navigate, onLogout]);

  const handleHelpClick = () => {
    const url = userInfo
      ? "/Help Document for GCTO Service Provider Portal - Merck User.pdf"
      : "/Help Document for GCTO Service Provider Portal - Non Merck User.pdf";
    window.open(url, "_blank");
  };

  return (
    <>
      {!userInfo && loading && <PageLoader />}
      <Disclosure as={"nav"} className="bg-grey-800 rounded-none pb-16">
        <div className="relative flex h-16 items-center justify-between rounded-none content-between my-6 mt-0">
          <div className="flex-1">
            <button onClick={() => navigate("/")} aria-labelledby="logo">
              <img
                src="/images/merck_logo.png"
                className="h-20 w-auto"
                alt="logo-image"
              />
            </button>
          </div>
          {userInfo &&
            (userInfo.role === "Admin" || userInfo.role === "Super_Admin") &&
            location.pathname !== "/admin" && (
              <div className="h-full w-28 flex items-center justify-center mr-4">
                <button
                  className="text-teal-600 font-bold hover:text-teal-700"
                  data-testid="go-to-admin"
                  onClick={() => navigate("/admin")}
                >
                  {FORM_TEXTS.ADMIN_TEXT}
                </button>
              </div>
            )}

          <div className="h-full w-16 flex items-center justify-center bg-teal-600 mr-4 text-white font-bold">
            <div className="flex flex-col items-center">
              <HelpIcon />
              <button data-testid="help" onClick={handleHelpClick}>
                {FORM_TEXTS.HELP_TEXT}
              </button>
            </div>
          </div>

          {userInfo?.name ? (
            <div className="bg-teal-600 h-full w-96 flex items-center">
              <div className="inline-flex text-white font-bold text-xl w-full">
                <p className="ml-auto mr-8">{userInfo?.name}</p>
                <button
                  className="mr-8"
                  data-testid="logout-button"
                  onClick={() => onLogout("/")}
                >
                  <LogoutIcon />
                </button>
              </div>
            </div>
          ) : (
            location.pathname === "/" && (
              <div className="bg-teal-600 h-full w-96 flex items-center">
                <button
                  onClick={onLogin}
                  className="ml-auto mr-auto"
                  data-testid="merck-employee"
                >
                  <p className="inline-flex text-white text-center font-bold text-xl">
                    {FORM_TEXTS.MSD_EMPLOYEE}
                  </p>
                </button>
              </div>
            )
          )}
        </div>
      </Disclosure>
      <AddNotification
        message={notification?.message}
        type={notification?.type}
        show={notification?.show}
        onClose={() => dispatch(clearNotification())}
      />
    </>
  );
};
